import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { PlusCircle, Bold, Italic, Link as LinkIcon, List, ListOrdered, Building2, Calendar, ClipboardList, ArrowLeft, FileText, UserPlus, Users, Sparkles, Settings, ChevronLeft, Mail, Phone, MapPin, Hash, MessageSquare, User, X, Video, Clock, Check, AlertTriangle, CalendarCheck } from 'lucide-react';
import ChatGptPromptModal from './ChatGptPromptModal';
import { useClientStore } from '../../lib/stores/clientStore';
import { useServiceStore } from '../../lib/stores/serviceStore';
import { usePaymentMethodStore } from '../../lib/stores/paymentMethodStore';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { useDebouncedCallback } from '../../lib/hooks/useDebounce';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { useNextQuoteNumber } from '../../lib/hooks/useNextQuoteNumber';
import { useNavigate } from 'react-router-dom';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { CreateClientDrawer } from '../clients/create-drawer/CreateClientDrawer';
import { ClientQuickInput, QuickClientData } from '../clients/ClientQuickInput';
import { ClientContactsManager } from '../clients/ClientContactsManager';
import { PaymentScheduleCalculator } from './PaymentScheduleCalculator';
import { EnhancedDealServiceSelector } from './EnhancedDealServiceSelector';
import { DealFormHeader } from './DealFormHeader';
import type { Deal, DealFormData, DealServiceFormData, DealPaymentInstallment, DealReferrer, DealCommissionOverrideFormData, DealVariable, ClientContact, Client } from '../../lib/types';
import { dealsTranslations } from '../../lib/i18n/translations/deals';
// Removed: fuzzy search now handled server-side
import { useWhatsAppAutomationStore } from '../../lib/stores/whatsAppAutomationStore';
import { useApiConfigurationStore } from '../../lib/stores/apiConfigurationStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useReferrerStore } from '../../lib/stores/referrerStore';
import { useUserStore } from '../../lib/stores/userStore';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useActivityTypesStore, getActivityTypeLabel as getActivityTypeLabelFromStore } from '../../lib/stores/activityTypesStore';
import { useTheme } from '../../contexts/ThemeContext';
import { usePermissions } from '../../lib/hooks/usePermissions';
import type { MeetingRecording } from '../../lib/types/meetingRecording';
import { useDealClientFeed } from '../../lib/hooks/deals/useDealClientFeed';
import { useDealLocalContacts } from '../../lib/hooks/deals/useDealLocalContacts';
import { useDealWhatsAppConfig } from '../../lib/hooks/deals/useDealWhatsAppConfig';
import { DealStep1BillingEmpty } from './form/DealStep1BillingEmpty';
import { DealStep1OfferDetailsPanel } from './form/DealStep1OfferDetailsPanel';
import { DealClientFeedTimeline } from './form/DealClientFeedTimeline';
import { DealStep1CommissionsTab } from './form/DealStep1CommissionsTab';
import { DealStep2BillingTab } from './form/DealStep2BillingTab';
import { DealClientSelectorCard } from './form/DealClientSelectorCard';
import { loadDealDraft, saveDealDraft, clearDealDraft } from '../../lib/deals/dealDraft';
import { validateDealServices, formatValidationErrorMessage } from '../../lib/deals/validateDealServices';
import { fetchProductAvailability, findOverAvailable, type OverAvailableLine } from '../../lib/deals/stockAvailability';
import {
  cleanWarehouseInfo,
  isWarehouseConfigActive,
  missingWarehouseFields,
  normalizeWarehouseConfig,
  type DealWarehouseInfo,
} from '../../lib/deals/warehouseFields';
import { DealWarehouseFields } from './form/DealWarehouseFields';
import { DealReferrerSection } from '../referrers/DealReferrerSection';
import { supabase } from '../../lib/supabase';
import { DealShippingLocationSelect } from './form/DealShippingLocationSelect';
import { DealClientVatToggle } from './form/DealClientVatToggle';
import { useWarehouseSettings } from '../../lib/hooks/useWarehouseSettings';
import { getVatCode } from '../../lib/services/vatCodeService';
import { useDbColumnSupported } from '../../lib/hooks/useDbColumnSupported';
import { DealCommissionSection } from './DealCommissionSection';
// Variables are now managed per service in the EnhancedDealServiceSelector
//import { terminal } from 'virtual:terminal';


interface DealFormProps {
  clientId?: string;
  leadId?: string; // Add leadId prop
  initialData?: Deal;
  onSubmit: (data: DealFormData, services: DealServiceFormData[], commissions?: DealCommissionOverrideFormData, referrerIds?: string[], contactIds?: string[]) => void;
  onCancel: () => void;
}

// Constants
const USE_SERVICE_AS_TITLE_KEY = 'deal_use_service_as_title';

export function DealForm({ clientId, leadId, initialData, onSubmit, onCancel }: DealFormProps) {
  const navigate = useNavigate();
  const { clients, leads, selectableClients, loading: clientsLoading, fetchClientsForSelect, fetchLeads, updateClient, searchClientsAndLeads, fetchClientById } = useClientStore();

  // Removed useUserFilter - now using server-side search for the dropdown

  // Debug leads loading
  useEffect(() => {
    console.log('🔍 Debug leads:', { 
      leadsLength: leads.length, 
      clientsLength: clients.length,
      clientsLoading,
      leads: leads.slice(0, 3), // Show first 3 leads
      // Check if there are clients that could be leads
      clientsWithLeadState: clients.filter(c => c.client_state?.is_lead).length
    });
  }, [leads, clients, clientsLoading]);
  const { services, fetchServices } = useServiceStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const { dealServices, fetchDealServices } = useDealServiceStore();
  const { notes, loading: notesLoading, fetchNotesByLeadId, updateNote, deleteNote } = useLeadNoteStore();
  const { user } = useAuth();
  const { confirm, language, toast } = useApp();
  const { settings } = useSettingsStore();
  const { canFilterClientsByUser, loading: permissionsLoading } = usePermissions();
  const { isDark } = useTheme();
  const { customTypes, fetched: activityTypesFetched, fetchCustomTypes } = useActivityTypesStore();

  // Carica i tipi attività custom dell'org (una sola volta) per poter risolvere
  // lo slug → nome formattato nel badge della timeline (vedi getActivityTypeLabel sotto).
  useEffect(() => {
    if (!activityTypesFetched) {
      fetchCustomTypes();
    }
  }, [activityTypesFetched, fetchCustomTypes]);

  // Feed cliente (notes + activities + meeting recordings): hook isolato
  // in src/lib/hooks/deals/useDealClientFeed.ts. Le notes vengono caricate
  // nello store condiviso (useLeadNoteStore); activities/recordings
  // restano locali a questo hook e ritornate qui sotto.

  // State locale per mantenere il cliente caricato (evita che scompaia quando fetchLeads sovrascrive l'array)
  const [loadedClient, setLoadedClient] = useState<Client | null>(null);

  // Contatti locali: sincronizzazione con cliente + handler save → hook isolato
  // in src/lib/hooks/deals/useDealLocalContacts.ts (richiede `selectedClient` e
  // `setSelectedContactIds` che vengono dichiarati più sotto: la chiamata
  // effettiva avviene quindi dopo le rispettive dichiarazioni).

  // Stili React Select con supporto dark mode glass
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '42px',
      background: isDark ? 'rgba(255, 255, 255, 0.15)' : 'white',
      backdropFilter: isDark ? 'blur(12px)' : 'none',
      borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      '&:hover': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    }),
    menu: (base: any) => ({
      ...base,
      background: isDark ? 'rgba(30, 30, 30, 0.95)' : 'white',
      backdropFilter: isDark ? 'blur(16px)' : 'none',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '0.5rem',
      overflow: 'hidden',
      zIndex: 9999,
      boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
    }),
    menuList: (base: any) => ({
      ...base,
      padding: '0.5rem'
    }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)')
        : 'transparent',
      color: isDark ? 'white' : 'black',
      fontWeight: state.isFocused ? '600' : '400',
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      cursor: 'pointer'
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? 'white' : 'black',
      fontWeight: '500'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'
    }),
    input: (base: any) => ({
      ...base,
      color: isDark ? 'white' : 'black'
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '0.375rem'
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDark ? 'white' : 'black',
      fontWeight: '500'
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
      }
    })
  };

  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState<number | null>(null);
  // whatsAppConfigured + dealNotificationsEnabled → useDealWhatsAppConfig (sotto)
  const [showClientForm, setShowClientForm] = useState(false);
  const [showQuickClientInput, setShowQuickClientInput] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [initialClientFormData, setInitialClientFormData] = useState<{
    business_name?: string;
    entity_type?: 'company' | 'individual';
    email?: string;
    phone?: string;
    whatsapp_number?: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  // Tracking se la colonna sinistra sta scrollando: usato per applicare
  // bg/border solo quando la card Cliente è effettivamente "pinned" in cima
  // (sticky senza stile distintivo quando il contenuto entra senza scroll).
  const [leftColumnScrolled, setLeftColumnScrolled] = useState(false);
  const [hasUnsavedServiceChanges, setHasUnsavedServiceChanges] = useState(false);
  const [savedServiceIndex, setSavedServiceIndex] = useState<number | null>(null);

  // Ref per tracciare se abbiamo già fatto l'auto-selezione iniziale del servizio
  const firstLoadDone = useRef(false);
  // Offerta le cui righe sono già state caricate nel form: impedisce che un
  // refresh dello store sovrascriva il lavoro in corso (vedi effetto sotto).
  const mappedDealIdRef = useRef<string | null>(null);
  // Ref per tracciare il referrer_id pre-selezionato automaticamente dal cliente
  // Usato per gestire il cambio cliente (rimuovere vecchio referrer, aggiungere nuovo)
  const autoSelectedReferrerId = useRef<string | null>(null);
  const [useServiceAsTitle, setUseServiceAsTitle] = useState(() => {
    // Se siamo in modalità modifica, inizialmente impostiamo a false
    // (verrà aggiornato dal useEffect quando i servizi saranno caricati)
    if (initialData) {
      return false;
    }
    // Per nuove offerte, usa il valore salvato in localStorage
    const saved = localStorage.getItem(USE_SERVICE_AS_TITLE_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [commissionData, setCommissionDataInternal] = useState<DealCommissionOverrideFormData>({
    user_commission_type: 'percentage',
    referrer_commission_overrides: [],
    override_reason: ''
  });

  // Wrapper con log per debug
  const setCommissionData = (data: DealCommissionOverrideFormData) => {
    console.log('📊 [DealForm] setCommissionData called:', {
      referrer_overrides: data.referrer_commission_overrides,
      user_rate: data.user_commission_rate,
      user_type: data.user_commission_type
    });
    setCommissionDataInternal(data);
  };
  // Flag per tracciare se l'utente ha modificato le commissioni (condiviso tra tutte le istanze)
  const [hasUserEditedCommission, setHasUserEditedCommission] = useState(false);

  // Carica dealReferrerIds dalla bozza se disponibile (in creazione)
  const [dealReferrerIds, setDealReferrerIds] = useState<string[]>(() => {
    if (initialData) return [];
    const draft = loadDealDraft();
    if (draft && clientId && draft.formData?.client_id !== clientId) return [];
    return Array.isArray(draft?.dealReferrerIds) ? draft!.dealReferrerIds! : [];
  });
  const { rules, fetchRules } = useWhatsAppAutomationStore();

  // Reset commission data when switching from edit to create mode
  useEffect(() => {
    if (!initialData?.id) {
      // Se non stiamo modificando un'offerta esistente, resettiamo le commissioni
      setCommissionData({
        user_commission_type: 'percentage',
        referrer_commission_overrides: [],
        override_reason: ''
      });
    }
  }, [initialData?.id]);
  const { configurations, fetchConfigurations } = useApiConfigurationStore();
  const { referrers, fetchReferrers, fetchDealReferrers } = useReferrerStore();
  const { users = [], fetchUsers } = useUserStore();

  // Carica una bozza esistente: in modalità modifica (initialData presente)
  // ignoriamo il draft localStorage. Implementazione in lib/deals/dealDraft.
  // Offerta aperta per un Lead/Cliente preciso (es. "Nuova offerta" dalla chat): la bozza
  // vale solo se era dello stesso Lead/Cliente, altrimenti porterebbe servizi e titolo altrui.
  const savedDraft = initialData ? null : loadDealDraft();
  const initialDraft = savedDraft && clientId && savedDraft.formData?.client_id !== clientId ? null : savedDraft;

  const [currentStep, setCurrentStep] = useState(() => {
    if (initialDraft?.currentStep) return initialDraft.currentStep;
    return 1;  // Sempre step 1, sia in creazione che in modifica
  });

  // Tab attivo per Offerta/Commissioni/Segnalatori/Fatturazione
  const [activeCommissionTab, setActiveCommissionTab] = useState<'deal' | 'commissions' | 'referrers' | 'billing'>(() => {
    if (initialDraft?.activeCommissionTab) return initialDraft.activeCommissionTab;
    return 'deal';
  });

  // Stati per la modalità fattura unica
  const [singleInvoiceMode, setSingleInvoiceMode] = useState(() => {
    if (initialDraft?.singleInvoiceMode !== undefined) return initialDraft.singleInvoiceMode;
    return initialData?.single_invoice_mode ?? false;
  });

  const [singleInvoiceDate, setSingleInvoiceDate] = useState<Date | null>(() => {
    // Prima controlla se c'è una data nella bozza
    if (initialDraft?.singleInvoiceDate) {
      return new Date(initialDraft.singleInvoiceDate);
    }
    if (!initialData?.single_invoice_date) return null;
    // Parse date string as local date to avoid timezone shift
    const [year, month, day] = initialData.single_invoice_date.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  // Stato per override decurtazione commissioni a livello di offerta
  const [overrideCommissionScaling, setOverrideCommissionScaling] = useState<boolean | null>(() => {
    if (initialDraft?.overrideCommissionScaling !== undefined) return initialDraft.overrideCommissionScaling;
    return initialData?.override_commission_scaling ?? null;
  });

  // State per i contatti selezionati da associare all'offerta
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(() => {
    // Prima controlla se c'è un draft salvato
    if (initialDraft?.selectedContactIds && initialDraft.selectedContactIds.length > 0) {
      console.log('🔍 [DealForm] Ripristino selectedContactIds dal draft:', initialDraft.selectedContactIds);
      return initialDraft.selectedContactIds;
    }
    // Poi controlla i deal_contacts esistenti (modalità modifica) — id persona (modello nuovo)
    if (initialData?.deal_contacts && initialData.deal_contacts.length > 0) {
      const ids = initialData.deal_contacts.map(dc => dc.person_client_id).filter((x): x is string => !!x);
      console.log('🔍 [DealForm] selectedContactIds da initialData:', ids);
      return ids;
    }
    return [];
  });

  // Stato per la ricerca cliente nel dropdown (solo il setter è usato per tracciare l'input)
  const [, setClientSearchInput] = useState('');

  // Stati per la ricerca server-side
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Stato che indica se stiamo riprendendo un deal salvato in locale
  const [isResumingDraft, setIsResumingDraft] = useState(false);

  // Get translations based on current language
  const t = dealsTranslations[language as keyof typeof dealsTranslations] || dealsTranslations.it;

  // Helper per formattare date come in LeadNotesSection
  const formatDateNote = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActivityIcon = (activity: any) => {
    if (!activity.type) return <ClipboardList className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
    switch(activity.type) {
      case 'call': return <Phone className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      case 'videochiamata': return <Video className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      case 'appuntamento': return <CalendarCheck className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      case 'sopralluogo': return <MapPin className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      case 'email': return <Mail className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      case 'note': return <FileText className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
      default: return <ClipboardList className="h-3 w-3 text-[#D9730D] dark:text-orange-300" />;
    }
  };

  const getActivityStatusClass = (status: string) => {
    if (status === 'completed') return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
    if (status === 'overdue') return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
    return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300';
  };

  const getActivityTypeLabel = (type: string | null | undefined): string => {
    if (!type) return 'Task';
    return getActivityTypeLabelFromStore(type, customTypes);
  };

  // Funzioni per salvare/caricare deal dal localStorage
  // Timestamp ultimo autosave bozza (per indicatore UI "Bozza salvata Xs fa")
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  // Wrapper su saveDealDraft che aggiorna anche il timestamp UI "salvata Xs fa".
  const saveDraftToLocalStorage = useCallback((
    draftFormData: any,
    draftServiceItems: any,
    additionalData?: {
      currentStep?: number;
      activeCommissionTab?: 'deal' | 'commissions' | 'referrers' | 'billing';
      dealReferrerIds?: string[];
      singleInvoiceMode?: boolean;
      singleInvoiceDate?: Date | null;
      overrideCommissionScaling?: boolean | null;
      selectedContactIds?: string[];
    }
  ) => {
    saveDealDraft(draftFormData, draftServiceItems, additionalData);
    setDraftSavedAt(Date.now());
  }, []);

  const loadDraftFromLocalStorage = useCallback(loadDealDraft, []);
  const clearDraftFromLocalStorage = useCallback(clearDealDraft, []);

  // Le funzioni handleCreateClient e handleClientFormSubmit sono già definite nel codice

  // Ottieni il prossimo numero preventivo disponibile dal server
  const { 
    nextQuoteNumber, 
    loading: loadingQuoteNumber, 
    debugInfo: quoteNumberDebugInfo,
    refreshQuoteNumber  // Include la funzione per forzare un refresh
  } = useNextQuoteNumber();
  
  // Log dettagliato dei dati di debug del numero preventivo
  useEffect(() => {
    if (quoteNumberDebugInfo) {
      console.log('🧬 DIAGNOSTICA NUMERO PREVENTIVO:', {
        ...quoteNumberDebugInfo,
        nextQuoteNumber,
        loadingQuoteNumber
      });
    }
  }, [quoteNumberDebugInfo, nextQuoteNumber, loadingQuoteNumber]);
  
  // Rimuoviamo il refresh automatico che sta causando loop infiniti
  // Questo risolve il problema mantenendo il funzionamento normale del hook
  
  // Mostriamo un numero temporaneo durante il caricamento
  const generateDefaultQuoteNumber = () => {
    // Se siamo in modalità edit di un deal esistente e non in bozza, mantieni il numero esistente
    if (initialData?.id && initialData?.quote_number) {
      console.log('📌 Usando numero preventivo esistente:', initialData.quote_number);
      return initialData.quote_number;
    }
    
    // Altrimenti, usa il prossimo numero disponibile calcolato dal server
    // Questo viene usato sia per nuovi deal che per bozze in fase di modifica
    if (nextQuoteNumber) {
      console.log('📌 Usando prossimo numero disponibile calcolato (senza incrementare):', nextQuoteNumber);
      return nextQuoteNumber;
    }
    
    // Durante il caricamento del numero, mostra un placeholder
    console.log('📌 Numero preventivo in caricamento:', loadingQuoteNumber);
    return loadingQuoteNumber ? "Caricamento..." : "";
  };
  
  // Teniamo traccia di quando la bozza è stata aggiornata l'ultima volta
  const [draftLastUpdated, setDraftLastUpdated] = useState<string | null>(() => {
    if (initialDraft?.lastUpdated) {
      const date = new Date(initialDraft.lastUpdated);
      return date.toLocaleString();
    }
    return null;
  });
  
  // Aggiorna il numero preventivo quando viene caricato dal server
  useEffect(() => {
    // Se abbiamo il numero e non è un deal esistente, aggiorniamo il campo
    if (nextQuoteNumber && (!initialData?.id || !initialData?.quote_number)) {
      console.log('🔄 Aggiornamento numero preventivo nel form con valore precaricato:', nextQuoteNumber);
      setFormData(prevData => ({
        ...prevData,
        quote_number: nextQuoteNumber
      }));
    }
  }, [nextQuoteNumber, initialData]);

  const [formData, setFormData] = useState<DealFormData>(() => {
    // Se c'è una bozza e non siamo in modalità modifica, carichiamo i dati dalla bozza
    if (initialDraft && !initialData) {
      setIsResumingDraft(true);
      // Manteniamo la bozza ma assicuriamoci di avere un numero preventivo aggiornato
      return {
        ...initialDraft.formData,
        // Update client_id and lead_id if passed as props
        client_id: clientId || initialDraft.formData.client_id || '',
        lead_id: leadId || initialDraft.formData.lead_id || '',
        // Assegna all'utente corrente se non specificato nella bozza
        assigned_to: initialDraft.formData.assigned_to || user?.id || '',
        // Sovrascriviamo sempre il campo quote_number con l'ultimo disponibile
        // In questo modo la bozza avrà sempre il numero più aggiornato
        quote_number: "Caricamento...", // Verrà aggiornato da useEffect sotto
        closing_probability: initialDraft.formData.closing_probability || 5
      };
    }

    return {
      id: initialData?.id,
      client_id: clientId || initialData?.client_id || '',
      lead_id: leadId || initialData?.lead_id || '',
      title: initialData?.title || '',
      description: initialData?.description || '',
      assigned_to: initialData?.assigned_to || user?.id || '',
      status: initialData?.status || 'draft',
      send_notifications: initialData?.send_notifications !== false,
      created_at: initialData?.created_at ? new Date(initialData.created_at) : new Date(),
      quote_number: generateDefaultQuoteNumber(),
      closing_probability: initialData?.closing_probability || 5,
      offer_reference: initialData?.offer_reference || '',
      offer_path: initialData?.offer_path || '',
      payment_method_id: (initialData as any)?.payment_method_id ?? null
    };
  });

  const [serviceItems, setServiceItems] = useState<DealServiceFormData[]>(() => {
    // Se c'è una bozza e non siamo in modalità modifica, carichiamo i servizi dalla bozza
    if (initialDraft && !initialData && Array.isArray(initialDraft.serviceItems) && initialDraft.serviceItems.length > 0) {
      return initialDraft.serviceItems;
    }

    // Non inizializzare con un servizio vuoto per evitare errori di validazione
    return [];
  });

  const calculateTotalAmount = () => {
    return serviceItems.reduce((total, service) => {
      const baseAmount = service.discounted_amount || service.amount;
      
      // Se è un servizio mensile e ha una durata specificata (> 0), moltiplica per il numero di mesi
      const isMonthlyService = services.find(s => s.id === service.service_id)?.service_type === 'monthly';
      if (isMonthlyService && service.monthly_duration && service.monthly_duration > 0) {
        return total + (baseAmount * service.monthly_duration);
      }
      
      // Altrimenti ritorna l'importo base
      return total + baseAmount;
    }, 0);
  };

  const safeLog = (message, ...args) => {
  try {
    if (typeof terminal !== 'undefined' && console.log) {
      console.log(message, ...args);
    } else {
      console.log(message, ...args);
    }
  } catch (e) {
    console.log('Logging error:', e);
    console.log(message, ...args);
  }
};


  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-700 underline',
        },
      }),
      Placeholder.configure({
        placeholder: 'Scrivi una descrizione dettagliata dell\'offerta...',
      }),
    ],
    content: initialData?.description || '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        description: editor.getHTML()
      }));
    },
  });

  // Fetch clients, services, payment methods, and deal services
  // NOTA: fetchLeads() rimosso - non necessario per creare offerte e sovrascriveva l'array leads del Kanban
  useEffect(() => {
    // Fetch lightweight client list for dropdown
    fetchClientsForSelect();
    fetchServices();
    fetchPaymentMethods();
    fetchReferrers();
    fetchUsers();

    if (initialData?.id) {
      fetchDealServices(initialData.id);
      // Load deal referrers immediately so commission tab can show referrer commissions
      fetchDealReferrers(initialData.id).then(loadedReferrers => {
        const referrerIds = loadedReferrers.map(r => r.referrer_id);
        setDealReferrerIds(referrerIds);

        // Carica le commissioni esistenti in commissionData per edit mode
        const referrerOverrides = loadedReferrers
          .filter(r => r.commission_rate !== null)
          .map(r => ({
            referrer_id: r.referrer_id,
            commission_rate: r.commission_rate,
            commission_type: 'percentage' as const,
            source: 'custom' as const
          }));

        if (referrerOverrides.length > 0) {
          setCommissionData(prev => ({
            ...prev,
            referrer_commission_overrides: referrerOverrides
          }));
        }
      });
    }
  }, [initialData, fetchClientsForSelect, fetchServices, fetchPaymentMethods, fetchDealServices, fetchReferrers, fetchDealReferrers, fetchUsers, clientId]);

  // Update client_id in formData when clientId prop changes (only for new deals, not in edit mode)
  useEffect(() => {
    if (clientId && clientId !== formData.client_id && !initialData) {
      console.log('🔄 Updating formData.client_id:', { clientId, currentClientId: formData.client_id });
      setFormData(prev => ({
        ...prev,
        client_id: clientId
      }));
    }
  }, [clientId, formData.client_id, initialData]);

  // Pre-select assigned user based on client's assigned agent (only for new deals).
  // Legge fresh dal DB: lo store in-memory clients/leads può essere stale dopo modifica
  // del responsabile in anagrafica, causando il salvataggio del deal con il vecchio responsabile.
  useEffect(() => {
    if (initialData) return;
    if (!formData.client_id) return;

    let cancelled = false;
    const clientIdSnapshot = formData.client_id;

    (async () => {
      let assignedTo: string | null = null;
      let resolved = false;
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('assigned_to')
          .eq('id', clientIdSnapshot)
          .maybeSingle();
        if (!error && data) {
          assignedTo = data.assigned_to ?? null;
          resolved = true;
        } else if (error) {
          console.warn('⚠️ Pre-fill assigned_to: query failed, falling back to cache:', error);
        }
      } catch (err) {
        console.warn('⚠️ Pre-fill assigned_to: exception, falling back to cache:', err);
      }

      if (!resolved) {
        const fallback = [...clients, ...leads].find(c => c.id === clientIdSnapshot);
        if (fallback) {
          assignedTo = fallback.assigned_to ?? null;
          resolved = true;
        }
      }

      if (cancelled || !resolved) return;

      if (assignedTo) {
        setFormData(prev => ({ ...prev, assigned_to: assignedTo }));
      } else if (user?.id) {
        setFormData(prev => ({ ...prev, assigned_to: user.id }));
      }
    })();

    return () => { cancelled = true; };
  }, [formData.client_id, user?.id, initialData]);

  // Pre-select referrer based on client's associated referrer (only for new deals).
  // Legge fresh dal DB per gli stessi motivi del pre-fill assigned_to.
  useEffect(() => {
    if (initialData) return;
    if (!formData.client_id) return;

    let cancelled = false;
    const clientIdSnapshot = formData.client_id;

    (async () => {
      let newReferrerId: string | null = null;
      let resolved = false;
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('referrer_id')
          .eq('id', clientIdSnapshot)
          .maybeSingle();
        if (!error && data) {
          newReferrerId = data.referrer_id ?? null;
          resolved = true;
        }
      } catch (err) {
        console.warn('⚠️ Pre-fill referrer: exception, falling back to cache:', err);
      }

      if (!resolved) {
        const fallback = [...clients, ...leads].find(c => c.id === clientIdSnapshot);
        if (fallback) {
          newReferrerId = fallback.referrer_id ?? null;
          resolved = true;
        }
      }

      if (cancelled || !resolved) return;

      const previousAutoSelectedId = autoSelectedReferrerId.current;
      if (newReferrerId === previousAutoSelectedId) return;

      setDealReferrerIds(prev => {
        let updated = [...prev];
        if (previousAutoSelectedId && updated.includes(previousAutoSelectedId)) {
          updated = updated.filter(id => id !== previousAutoSelectedId);
        }
        if (newReferrerId && !updated.includes(newReferrerId)) {
          updated = [...updated, newReferrerId];
        }
        return updated;
      });
      autoSelectedReferrerId.current = newReferrerId;
    })();

    return () => { cancelled = true; };
  }, [formData.client_id, initialData]);

  // Ref per avere sempre il valore aggiornato di canFilterClientsByUser nel callback debounced
  // (evita stale closure quando i permessi caricano in modo asincrono dopo il primo render)
  const filterByUserRef = useRef(false);
  useEffect(() => {
    if (!permissionsLoading) {
      filterByUserRef.current = canFilterClientsByUser;
    }
  }, [canFilterClientsByUser, permissionsLoading]);

  // Funzione di ricerca server-side con debounce
  // Applica filtro per permessi: se l'utente ha "vede solo i propri", filtra per assigned_to
  const debouncedSearch = useDebouncedCallback(
    async (term: string) => {
      if (term.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        // Legge dal ref per avere sempre il valore aggiornato (evita stale closure al primo render)
        const assignedToFilter = filterByUserRef.current && user?.id ? [user.id] : undefined;
        const results = await searchClientsAndLeads(term, assignedToFilter);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching clients/leads:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    300,
    [searchClientsAndLeads, canFilterClientsByUser, user?.id]
  );

  // Opzioni del dropdown basate sui risultati della ricerca server-side
  const filteredAndSortedClientOptions = useMemo(() => {
    return searchResults.map(item => ({
      value: item.id,
      label: item.client_state?.is_lead
        ? `${item.business_name} (Lead)`
        : item.business_name,
      type: item.client_state?.is_lead ? 'lead' : 'client',
      data: item
    }));
  }, [searchResults]);

  // Per retrocompatibilità con il codice esistente
  const allClientOptions = filteredAndSortedClientOptions;

  // Ref per tracciare se il cliente iniziale è stato caricato
  const initialClientLoaded = useRef(false);

  // Carica il cliente selezionato in edit mode (per mostrarlo nel dropdown)
  useEffect(() => {
    const loadSelectedClient = async () => {
      const clientIdToLoad = formData.client_id || clientId;
      if (clientIdToLoad && !initialClientLoaded.current) {
        initialClientLoaded.current = true;
        // Cerca prima nei dati già caricati
        const existingClient = [...clients, ...leads].find(c => c.id === clientIdToLoad);
        if (existingClient) {
          setSearchResults([existingClient]);
          // Salva nello stato locale per evitare che scompaia quando fetchLeads sovrascrive l'array
          setLoadedClient(existingClient as Client);
        } else {
          // Se non trovato, carica dal server
          try {
            await fetchClientById(clientIdToLoad);
            let fetchedClient = [...clients, ...leads].find(c => c.id === clientIdToLoad);
            // fetchClientById setta solo selectedClient nello store, non aggiorna clients/leads arrays
            // Fallback per lead non in cache (es. deal creati via MCP)
            if (!fetchedClient) {
              const storeState = useClientStore.getState();
              if (storeState.selectedClient?.id === clientIdToLoad) {
                fetchedClient = storeState.selectedClient;
              }
            }
            if (fetchedClient) {
              setSearchResults([fetchedClient]);
              setLoadedClient(fetchedClient as Client);
            }
          } catch (error) {
            console.error('Error loading selected client:', error);
          }
        }
      }
    };
    loadSelectedClient();
  }, [formData.client_id, clientId, clients, leads, fetchClientById]);

  // Get selected client name for display in step 2
  const selectedClientName = React.useMemo(() => {
    if (!formData.client_id) return '';
    const client = [...clients, ...leads].find(c => c.id === formData.client_id);
    return client?.business_name || '';
  }, [formData.client_id, clients, leads]);

  // Get full selected client data for display in left column
  // Usa loadedClient come fallback quando fetchLeads sovrascrive l'array e il cliente non è più presente
  const selectedClient = React.useMemo(() => {
    if (!formData.client_id) return null;
    // Prima cerca negli array dello store
    const fromStore = [...clients, ...leads].find(c => c.id === formData.client_id);
    if (fromStore) return fromStore;
    // Fallback: usa il cliente caricato localmente (evita che scompaia quando fetchLeads resetta l'array)
    if (loadedClient && loadedClient.id === formData.client_id) return loadedClient;
    return null;
  }, [formData.client_id, clients, leads, loadedClient]);

  // Debug client selection
  useEffect(() => {
    const selectedClient = [...clients, ...leads].find(client => client.id === (clientId || formData.client_id));
    console.log('🔍 Client selection debug:', {
      clientId,
      formDataClientId: formData.client_id,
      clientsCount: clients.length,
      leadsCount: leads.length,
      selectedClient: selectedClient ? { id: selectedClient.id, name: selectedClient.business_name, type: selectedClient.stato_cliente } : null,
      clientsLoading
    });
  }, [clientId, formData.client_id, clients, leads, clientsLoading]);

  // Indirizzo di spedizione dell'offerta (tra quelli del cliente). Il campo si
  // mostra e si salva solo se la colonna deals.shipping_location_id esiste:
  // prima della migration 20260916b il form resta identico e non invia la chiave.
  const [shippingLocationSupported, setShippingLocationSupported] = useState(false);
  const shippingLocationClientChangedRef = useRef(false);
  const shippingLocationPrevClientRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    supabase.from('deals').select('shipping_location_id').limit(1).then(({ error }) => {
      if (cancelled || error) return;
      setShippingLocationSupported(true);
      // In modifica si riprende il valore salvato
      if (initialData?.id) {
        supabase.from('deals').select('shipping_location_id').eq('id', initialData.id).maybeSingle().then(({ data }) => {
          if (cancelled || !data) return;
          setFormData(prev => ({ ...prev, shipping_location_id: (data as { shipping_location_id: string | null }).shipping_location_id ?? null }));
        });
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);
  // Cambio cliente: l'indirizzo del cliente precedente non vale più, si riparte dal predefinito del nuovo
  useEffect(() => {
    const prev = shippingLocationPrevClientRef.current;
    shippingLocationPrevClientRef.current = formData.client_id;
    if (!shippingLocationSupported || prev === undefined || prev === formData.client_id) return;
    shippingLocationClientChangedRef.current = true;
    setFormData(p => ({ ...p, shipping_location_id: null }));
  }, [formData.client_id, shippingLocationSupported]);

  const handleShippingLocationChange = useCallback(
    (id: string | null) => setFormData(prev => ({ ...prev, shipping_location_id: id })),
    [],
  );
  // IVA del cliente (clients.default_vat_rate): con «Applica a tutte le righe»
  // ogni riga prende quell'aliquota e non si cambia singolarmente
  // (deals.apply_client_vat). Tutto solo dopo la migration 20260916d.
  const clientVatSupported = useDbColumnSupported('deals', 'apply_client_vat');
  const [clientVatRate, setClientVatRate] = useState<number | null>(null);
  const [clientVatLoaded, setClientVatLoaded] = useState(false);
  // Scelta salvata sull'offerta caricata: il codice IVA del cliente (sotto)
  // decide solo dopo averla letta, altrimenti questa la sovrascriverebbe
  const [savedClientVatLoaded, setSavedClientVatLoaded] = useState(!initialData?.id);
  useEffect(() => {
    if (!clientVatSupported || !initialData?.id) return;
    let cancelled = false;
    supabase.from('deals').select('apply_client_vat').eq('id', initialData.id).maybeSingle().then(({ data }) => {
      if (cancelled) return;
      if (data) setFormData(prev => ({ ...prev, apply_client_vat: !!(data as { apply_client_vat: boolean }).apply_client_vat }));
      setSavedClientVatLoaded(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVatSupported, initialData?.id]);
  useEffect(() => {
    setClientVatLoaded(false);
    setClientVatRate(null);
    if (!clientVatSupported || !formData.client_id) return;
    let cancelled = false;
    supabase.from('clients').select('default_vat_rate').eq('id', formData.client_id).maybeSingle().then(({ data, error }) => {
      if (cancelled) return;
      const raw = (data as { default_vat_rate: number | null } | null)?.default_vat_rate;
      const rate = !error && raw != null ? Number(raw) : null;
      setClientVatRate(rate);
      setClientVatLoaded(true);
      // Cliente senza aliquota: l'opzione non si può applicare
      if (rate == null) setFormData(prev => (prev.apply_client_vat ? { ...prev, apply_client_vat: false } : prev));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVatSupported, formData.client_id]);
  // Codice IVA del cliente (esenzione, es. NI41): solo org con «Codici IVA dei
  // clienti» attivo. Sulle offerte in compilazione (nuove, in Bozza, o con il
  // cliente appena cambiato) l'aliquota del codice si applica da sola a tutte
  // le righe; resta togglabile. Inviate/accettate non si toccano.
  const vatCodeColumnSupported = useDbColumnSupported('clients', 'vat_code_id');
  const { client_vat_codes_enabled: vatCodesEnabled } = useWarehouseSettings();
  const [clientVatCodeLabel, setClientVatCodeLabel] = useState<string | null>(null);
  useEffect(() => {
    setClientVatCodeLabel(null);
    if (!clientVatSupported || !vatCodeColumnSupported || !vatCodesEnabled || !formData.client_id || !savedClientVatLoaded) return;
    const autoApply = !initialData?.id
      || (initialData.status || 'draft') === 'draft'
      || formData.client_id !== initialData.client_id;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('clients').select('vat_code_id').eq('id', formData.client_id).maybeSingle();
      const codeId = (data as { vat_code_id: string | null } | null)?.vat_code_id;
      const code = codeId ? await getVatCode(codeId) : null;
      if (cancelled || !code) return;
      setClientVatCodeLabel(`${code.code} · ${code.description}`);
      if (autoApply) {
        setFormData(prev => (prev.apply_client_vat ? prev : { ...prev, apply_client_vat: true }));
        setServiceItems(prev => prev.map(s => ({ ...s, vat_rate: Number(code.rate) } as typeof s)));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVatSupported, vatCodeColumnSupported, vatCodesEnabled, formData.client_id, savedClientVatLoaded]);
  const clientVatApplied = clientVatSupported && !!formData.apply_client_vat && clientVatRate != null;
  const handleClientVatToggle = useCallback((checked: boolean) => {
    setFormData(prev => ({ ...prev, apply_client_vat: checked }));
    if (checked && clientVatRate != null) {
      setServiceItems(prev => prev.map(s => ({ ...s, vat_rate: clientVatRate } as typeof s)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVatRate]);

  // Indicazioni per il magazzino (packaging, pagamento, urgenza, note):
  // campi e obblighi decisi in Impostazioni → Offerte. Spenta finché
  // l'organizzazione non attiva almeno un campo o manca la migration 20261001.
  const warehouseInfoSupported = useDbColumnSupported('deals', 'warehouse_info');
  const warehouseConfig = React.useMemo(
    () => normalizeWarehouseConfig((settings as any)?.deal_warehouse_fields),
    [settings],
  );
  const warehouseActive = warehouseInfoSupported && isWarehouseConfigActive(warehouseConfig);
  useEffect(() => {
    if (!warehouseInfoSupported || !initialData?.id) return;
    let cancelled = false;
    supabase.from('deals').select('warehouse_info').eq('id', initialData.id).maybeSingle().then(({ data }) => {
      if (cancelled || !data) return;
      setFormData(prev => ({ ...prev, warehouse_info: (data as { warehouse_info: DealWarehouseInfo | null }).warehouse_info ?? null }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseInfoSupported, initialData?.id]);
  const handleWarehouseInfoChange = useCallback(
    (info: DealWarehouseInfo) => setFormData(prev => ({ ...prev, warehouse_info: info })),
    [],
  );

  const shippingLocationSlot = formData.client_id && (shippingLocationSupported || warehouseActive || (clientVatSupported && clientVatRate != null)) ? (
    <>
      {shippingLocationSupported && (
        <DealShippingLocationSelect
          clientId={formData.client_id}
          value={formData.shipping_location_id}
          onChange={handleShippingLocationChange}
          autoSelectDefault={!initialData?.id || shippingLocationClientChangedRef.current}
          required={warehouseInfoSupported && warehouseConfig.shipping_address.mode === 'required'}
          clientAddress={
            selectedClient?.street_address?.trim() && selectedClient?.city?.trim()
              ? [[selectedClient.street_address, (selectedClient as { street_number?: string | null }).street_number].filter(Boolean).join(' '), [selectedClient.postal_code, selectedClient.city].filter(Boolean).join(' ')].join(', ')
              : ''
          }
        />
      )}
      {warehouseActive && (
        <DealWarehouseFields
          config={warehouseConfig}
          value={formData.warehouse_info}
          onChange={handleWarehouseInfoChange}
        />
      )}
      {clientVatSupported && clientVatRate != null && (
        <DealClientVatToggle
          rate={clientVatRate}
          codeLabel={clientVatCodeLabel}
          checked={!!formData.apply_client_vat}
          onChange={handleClientVatToggle}
        />
      )}
    </>
  ) : null;

  // Feed cliente: notes (via store) + activities + meeting recordings.
  // Hook isolato → src/lib/hooks/deals/useDealClientFeed.ts
  const { clientActivities, activitiesLoading, clientRecordings } = useDealClientFeed(
    formData.client_id || clientId,
    fetchNotesByLeadId
  );

  // Contatti locali: sync DB → state + reset selezione su cambio cliente +
  // mapping temp_* → ID reali. Implementazione in
  // Carica i referenti selezionati (id persona) quando si modifica un deal esistente
  useEffect(() => {
    if (initialData?.deal_contacts && initialData.deal_contacts.length > 0) {
      const contactIds = initialData.deal_contacts.map(dc => dc.person_client_id).filter((x): x is string => !!x);
      setSelectedContactIds(contactIds);
    }
  }, [initialData?.id, initialData?.deal_contacts]);

  // Gestione retrocompatibilità per titoli personalizzati in modalità modifica
  useEffect(() => {
    // Solo per offerte in modifica (non nuove)
    if (!initialData || !formData.title || services.length === 0) {
      return;
    }

    // Verifica se il titolo corrisponde a un servizio del catalogo
    const matchingService = services.find(s => s.title === formData.title);

    if (matchingService) {
      // Il titolo corrisponde a un servizio → abilita la checkbox
      console.log('📝 Titolo corrisponde a servizio del catalogo:', matchingService.title);
      setUseServiceAsTitle(true);
    } else {
      // Il titolo NON corrisponde → è personalizzato, disabilita la checkbox
      console.log('✏️ Titolo personalizzato rilevato:', formData.title);
      setUseServiceAsTitle(false);
    }
  }, [initialData, formData.title, services]);

  // Salvataggio automatico DEBOUNCED dei dati del form - evita sovrascritture durante la digitazione
  useEffect(() => {
    // Non salviamo nulla se siamo in modalità modifica
    if (initialData) return;

    // Non salviamo se siamo in fase di caricamento iniziale da draft
    if (isResumingDraft) {
      setIsResumingDraft(false);
      return;
    }

    // Verifichiamo se ci sono dati significativi da salvare
    const hasValidData = formData.client_id || formData.title.trim() || serviceItems.some(s => s.service_id);

    if (hasValidData) {
      // DEBOUNCE il salvataggio a 4 secondi per evitare interferenze durante la digitazione
      const timeoutId = setTimeout(() => {
        saveDraftToLocalStorage(formData, serviceItems, {
          currentStep,
          activeCommissionTab,
          dealReferrerIds,
          singleInvoiceMode,
          singleInvoiceDate,
          overrideCommissionScaling,
          selectedContactIds
        });
      }, 4000);

      return () => clearTimeout(timeoutId);
    }
  }, [formData, serviceItems, currentStep, activeCommissionTab, dealReferrerIds, singleInvoiceMode, singleInvoiceDate, overrideCommissionScaling, selectedContactIds, initialData, isResumingDraft, saveDraftToLocalStorage]);
  
  // WhatsApp config: fetch configurations + rules, derive flags → hook
  // in src/lib/hooks/deals/useDealWhatsAppConfig.ts
  const { whatsAppConfigured, dealNotificationsEnabled } = useDealWhatsAppConfig({
    configurations,
    rules,
    fetchConfigurations,
    fetchRules,
  });

  // Side-effect locale: quando WhatsApp non è disponibile spegniamo il flag
  // send_notifications. Tenuto qui (non nel hook) per evitare di passare
  // un setFormData inline che causerebbe un loop infinito di setState.
  useEffect(() => {
    if (!whatsAppConfigured || !dealNotificationsEnabled) {
      setFormData(prev => prev.send_notifications ? { ...prev, send_notifications: false } : prev);
    }
  }, [whatsAppConfigured, dealNotificationsEnabled]);

// Inizializzazione delle righe dal database. Gira UNA VOLTA per offerta: prima
// rimappava a ogni cambio di `dealServices`, e un refresh dello store arrivato
// mentre l'utente stava lavorando (realtime, refetch dopo un salvataggio)
// riportava `serviceItems` allo stato del DB — buttando via le righe appena
// aggiunte e gli add-on appena spuntati, che sparivano senza alcun segnale.
useEffect(() => {
  if (initialData?.id && dealServices.length > 0) {
    if (mappedDealIdRef.current === initialData.id) return;
    mappedDealIdRef.current = initialData.id;
    console.log('📝 Inizializzazione serviceItems da dealServices:', dealServices);

    const mappedServices = dealServices.map(service => {
      console.log('📋 Mapping service:', service.id, 'with variables:', service.variables?.length || 0);
      // Normalizziamo il payment_schedule per garantire un formato coerente
      let normalizedPaymentSchedule = null;

      if (service.payment_schedule) {
        console.log('🔍 Struttura payment_schedule originale:', service.payment_schedule);

        normalizedPaymentSchedule = {
          installments: Array.isArray(service.payment_schedule.installments)
            ? service.payment_schedule.installments.map(inst => ({
                type: inst.type || 'fixed',
                value: inst.value || 0,
                amount: inst.amount || 0,
                daysAfterStart: inst.daysAfterStart || 0,
                description: inst.description || '',
                isConfigurable: inst.isConfigurable || false,
                timeUnit: inst.timeUnit || 'days',
                fixedDayOfMonth: inst.fixedDayOfMonth,
                nextMonth: inst.nextMonth,
                fixedDate: inst.fixedDate,
                // Finup R21: preserva il flag "data da destinarsi"
                awaiting_approval: (inst as any).awaiting_approval ?? false
              }))
            : []
        };

        console.log('✅ Struttura payment_schedule normalizzata:', normalizedPaymentSchedule);
      }

      // Determiniamo se è un servizio personalizzato
      const isCustomService = service.service_id === null && service.custom_service_name;

      // Se è un servizio personalizzato, creiamo un service_id custom fittizio
      const serviceId = isCustomService
        ? `custom-${Math.floor(Math.random() * 100000)}`
        : service.service_id;

      console.log(`Servizio ${serviceId}: ${isCustomService ? 'PERSONALIZZATO' : 'standard'}`, {
        original_id: service.service_id,
        custom_name: service.custom_service_name,
        variables_count: service.variables?.length || 0,
        variables: service.variables
      });

      // Costruiamo l'oggetto service completo per i servizi personalizzati
      let serviceObj = null;
      let metadata = service.metadata;

      if (isCustomService) {
        // IMPORTANTE: Per i servizi personalizzati, leggi il service_type dal metadata se presente
        const serviceType = service.metadata?.custom_service_type || 'one_time';

        serviceObj = {
          id: serviceId,
          title: service.custom_service_name || 'Servizio personalizzato',
          description: service.description || '',
          service_type: serviceType
        };

        // Preserva il metadata dal database, aggiungendo original_custom_id se non c'è
        metadata = {
          ...(service.metadata || {}),
          original_custom_id: serviceId,
          custom_service_type: serviceType
        };

        console.log('🔍 [LOAD] Servizio personalizzato mappato:', {
          serviceId,
          custom_service_name: service.custom_service_name,
          metadata,
          service_type: serviceType
        });
      } else if (service.service) {
        // Per i servizi normali, copiamo l'oggetto service se presente
        serviceObj = service.service;
      }

      // VIVA: riga prodotto magazzino — riconosciuta da line_type='product' o presenza product_id
      const isProductLine = (service as any).line_type === 'product' || !!(service as any).product_id;

      // Mapping add-on dal DB → editingService.addons (vedi Plan B: snapshot
      // pattern; preserviamo service_addon_id quando presente per ricollegare
      // la riga al master del catalogo se serve invalidare cache).
      const mappedAddons = Array.isArray((service as any).addons)
        ? (service as any).addons.map((a: any) => ({
            service_addon_id: a.service_addon_id ?? undefined,
            name: a.name,
            description: a.description ?? undefined,
            amount: Number(a.amount ?? 0),
            is_required: !!a.is_required,
            is_included_in_base: !!a.is_included_in_base,
          }))
        : [];

      return {
        id: service.id, // Aggiungiamo l'ID del servizio dal database
        service_id: isProductLine ? `product-${(service as any).product_id}-${service.id}` : serviceId,
        payment_method_id: service.payment_method_id,
        amount: service.amount,
        discounted_amount: service.discounted_amount,
        description: service.description || '',
        invoice_description: (service as any).invoice_description ?? null,
        payment_schedule: normalizedPaymentSchedule,
        monthly_duration: service.monthly_duration,
        custom_service_name: service.custom_service_name || '',
        custom_service_type: isCustomService ? (service.metadata?.custom_service_type || 'one_time') : undefined,
        service: serviceObj,
        metadata: metadata,
        // Includiamo le variabili caricate dal database
        variables: service.variables || [],
        // Add-on selezionati per questa riga
        addons: mappedAddons,
        // VIVA campi product (preserva da DB se presenti)
        line_type: isProductLine ? 'product' : 'service',
        product_id: (service as any).product_id,
        quantity: (service as any).quantity,
        unit_of_measure: (service as any).unit_of_measure,
        unit_price: (service as any).unit_price,
        // IVA per-riga (preserva da DB: null = eredita da anagrafica, fallback 22)
        vat_rate: (service as any).vat_rate ?? null,
      };
    });

    if (mappedServices.length > 0) {
      console.log('✅ ServiceItems inizializzati:', mappedServices);
      console.log('🔍 ServiceItems variabili debug:', mappedServices.map(s => ({
        id: s.id,
        service_id: s.service_id,
        variables_count: s.variables?.length || 0,
        variables: s.variables
      })));
      setServiceItems(mappedServices);
    }
  }
}, [initialData, dealServices]);

// Metodo di pagamento dell'offerta: alla prima riga prodotto, se non e' ancora
// stato scelto, parte da quello predefinito del cliente — la stessa fonte che
// le righe servizio usano gia' come ripiego. Non tocca mai una scelta esistente,
// e su un'offerta di soli servizi non gira affatto.
useEffect(() => {
  const hasProductLines = serviceItems.some(s => (s as any).line_type === 'product');
  if (!hasProductLines || formData.payment_method_id || !formData.client_id) return;
  const client = clients.find(c => c.id === formData.client_id);
  if (client?.default_payment_method_id) {
    setFormData(prev => ({ ...prev, payment_method_id: client.default_payment_method_id }));
  }
}, [serviceItems, formData.payment_method_id, formData.client_id, clients]);

// Auto-select first service when editing a deal and entering step 2 (ONLY on first load)
useEffect(() => {
  // Solo se:
  // 1. Siamo in modalità modifica (initialData esiste)
  // 2. Siamo nello step 2 (servizi)
  // 3. Ci sono servizi caricati
  // 4. Non abbiamo già fatto l'auto-selezione iniziale
  if (initialData?.id && currentStep === 2 && serviceItems.length > 0 && !firstLoadDone.current) {
    console.log('🎯 Auto-selezione primo servizio in modalità modifica (primo caricamento)');
    setSelectedServiceIndex(0);
    firstLoadDone.current = true;
  }
}, [initialData?.id, currentStep, serviceItems.length]);

  const removeService = async (index: number) => {
    // Validazione indice
    if (index < 0 || index >= serviceItems.length) {
      console.error('❌ Indice servizio non valido:', index, 'serviceItems.length:', serviceItems.length);
      return;
    }

    const confirmed = await confirm({
      title: 'Rimuovi servizio',
      message: 'Sei sicuro di voler rimuovere questo servizio? Questa azione non può essere annullata.',
      confirmText: 'Rimuovi',
      cancelText: 'Annulla',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      const updatedServices = [...serviceItems];
      updatedServices.splice(index, 1);
      setServiceItems(updatedServices);
    }
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // Evita multipli submit
  if (isSaving) return;

  // NOTA 2026-06-06: rimosso il guard `hasUnsavedServiceChanges` che bloccava
  // il submit con uno shake invisibile. Il guard era ridondante perché
  // l'autosave (debounce 400ms) propaga già `editingService` → `selectedServices`
  // via `onChange()` (vedi EnhancedDealServiceSelector.tsx:1141). Quando l'utente
  // clicca "Salva Offerta", `serviceItems` contiene già lo stato aggiornato.
  // Il guard creava UX completamente "morta" (utente vedeva click senza effetto).

  // Se c'è un servizio selezionato ma senza modifiche non salvate, chiudi l'editor e procedi
  // (La validazione per modifiche non salvate è gestita sopra con hasUnsavedServiceChanges)
  if (selectedServiceIndex !== null) {
    setSelectedServiceIndex(null);
  }

  // Verifica che ci sia almeno un servizio
  if (serviceItems.length === 0) {
    toast.error(language === 'it' ? 'Aggiungi almeno un servizio all\'offerta' : 'Add at least one service to the deal');
    return;
  }

  setIsSaving(true);

  safeLog('🚀 handleSubmit chiamato');
  safeLog('🔍 serviceItems prima dell\'invio:', JSON.stringify(serviceItems, null, 2));

  try {
    // Validazioni standard. La logica di validazione per-riga (con regole
    // specifiche per prodotti vs servizi vs rate personalizzate) vive in
    // src/lib/deals/validateDealServices.ts — qui gestiamo solo l'effetto
    // collaterale UI (toast + jump al primo servizio incompleto).
    if (serviceItems.length > 0) {
      const validationErrors = validateDealServices(serviceItems);
      if (validationErrors.length > 0) {
        toast.error(formatValidationErrorMessage(validationErrors, language));
        setSelectedServiceIndex(validationErrors[0].index);
        setCurrentStep(2);
        setIsSaving(false);
        return;
      }
    } else {
      // Avvisa l'utente che non ci sono servizi
      const confirmEmptyServices = window.confirm(language === 'it'
        ? 'Non hai aggiunto servizi a questa offerta. Vuoi continuare comunque?'
        : 'No services have been added to this deal. Do you want to continue anyway?');
      if (!confirmEmptyServices) {
        setIsSaving(false);
        return;
      }
    }
  
    if (!formData.title.trim()) {
      toast.error(language === 'it' ? 'Inserisci un titolo per l\'offerta' : 'Please enter a deal title');
      setCurrentStep(1);
      setIsSaving(false);
      return;
    }

    // Indicazioni per il magazzino obbligatorie per l'organizzazione.
    // Indirizzo: uno scelto tra quelli del cliente, oppure la sede se compilata.
    if (warehouseActive) {
      const hasShippingAddress = !!formData.shipping_location_id
        || !!(selectedClient?.street_address?.trim() && selectedClient?.city?.trim());
      const missingWarehouse = missingWarehouseFields(warehouseConfig, formData.warehouse_info, hasShippingAddress);
      if (missingWarehouse.length > 0) {
        toast.error(`Indicazioni per il magazzino mancanti: ${missingWarehouse.join(', ')}`);
        setCurrentStep(1);
        setIsSaving(false);
        return;
      }
    }

    // Articoli a stock limitato: oltre il "Disponibile" della scheda articolo
    // si salva solo dopo una conferma esplicita. Se la lettura fallisce non
    // si blocca il salvataggio: l'avviso e' un aiuto, non un vincolo.
    const productIds = serviceItems
      .filter(s => s.line_type === 'product' && s.product_id)
      .map(s => s.product_id as string);
    if (productIds.length > 0) {
      let overAvailable: OverAvailableLine[] = [];
      try {
        overAvailable = findOverAvailable(serviceItems, await fetchProductAvailability(productIds));
      } catch (err) {
        console.error('Controllo disponibilità articoli non riuscito:', err);
      }
      if (overAvailable.length > 0) {
        const proceed = await confirm({
          title: 'Quantità oltre il disponibile',
          message: (
            <div className="space-y-2">
              <p>Questi articoli superano la quantità disponibile:</p>
              <ul className="list-disc pl-5 space-y-1">
                {overAvailable.map(o => (
                  <li key={o.productId}>
                    <strong>{o.name}</strong>: richiesti {o.requested}{o.unit ? ` ${o.unit}` : ''}, disponibili {o.available}
                  </li>
                ))}
              </ul>
              <p>Vuoi salvare l'offerta comunque?</p>
            </div>
          ),
          confirmText: 'Salva comunque',
          cancelText: 'Torna all\'offerta',
          confirmVariant: 'danger'
        });
        if (!proceed) {
          setCurrentStep(2);
          setIsSaving(false);
          return;
        }
      }
    }

    // Ensure we have the latest quote number from the hook before submission
    console.log('🧾 Quote number before submission:', {
      formData: formData.quote_number,
      hookValue: nextQuoteNumber,
      loading: loadingQuoteNumber
    });
    
    // If the hook has a value and the form doesn't, use the hook's value
    if (nextQuoteNumber && !formData.quote_number) {
      console.log(`📝 Updating form quote number to ${nextQuoteNumber} before submission`);
      setFormData(prev => ({
        ...prev,
        quote_number: nextQuoteNumber
      }));
    }
    
    // Crea una copia profonda di serviceItems per massima sicurezza
    const normalizedServices = JSON.parse(JSON.stringify(serviceItems.map(service => {
      // VIVA: riga prodotto magazzino — skip payment_schedule logic, mantieni campi product
      if (service.line_type === 'product') {
        return {
          ...service,
          // service_id transitorio 'product-*' va azzerato per non rompere FK services
          service_id: null,
          // Garantisci campi product presenti
          line_type: 'product',
          product_id: service.product_id,
          quantity: service.quantity,
          unit_of_measure: service.unit_of_measure,
          unit_price: service.unit_price,
          // payment_schedule vuoto (prodotti non hanno rate)
          payment_schedule: { installments: [] },
        };
      }

      // Verifica se è un servizio personalizzato
      const isCustomService = service.service_id?.toString().startsWith('custom-');
      
      // Se è un servizio personalizzato, assicurati che abbia il campo custom_service_name e service.title correttamente impostati
      if (isCustomService) {
        // Crea una copia base del servizio
        const normalizedService = { ...service };

        // Se non è specificato custom_service_name ma c'è service.title, usalo come fallback
        if (!normalizedService.custom_service_name && normalizedService.service?.title) {
          normalizedService.custom_service_name = normalizedService.service.title;
        }

        // IMPORTANTE: Per i servizi personalizzati, assicuriamoci che metadata contenga custom_service_type
        // Recupera il tipo di servizio da diverse fonti con ordine di priorità
        const serviceType = normalizedService.custom_service_type ||
                           normalizedService.service?.service_type ||
                           normalizedService.metadata?.custom_service_type ||
                           'one_time';

        // Crea o aggiorna il metadata con custom_service_type
        normalizedService.metadata = {
          ...(normalizedService.metadata || {}),
          custom_service_type: serviceType,
          original_custom_id: normalizedService.service_id
        };

        // Viceversa, se non c'è service.title ma c'è custom_service_name, crealo
        if (!normalizedService.service || !normalizedService.service.title) {
          if (!normalizedService.service) {
            normalizedService.service = {
              id: normalizedService.service_id,
              title: normalizedService.custom_service_name || 'Servizio personalizzato',
              description: '',
              service_type: serviceType
            };
          } else if (normalizedService.custom_service_name) {
            normalizedService.service.title = normalizedService.custom_service_name;
            normalizedService.service.service_type = serviceType;
          }
        }
        
        // Rimossa la logica che impediva di avere amount a 0
        // Ora permettiamo di salvare servizi con prezzo 0 euro
        
        console.log("🧰 Normalizzazione servizio personalizzato:", {
          service_id: normalizedService.service_id,
          custom_service_name: normalizedService.custom_service_name,
          service_title: normalizedService.service?.title,
          service_type: normalizedService.service?.service_type,
          metadata_type: normalizedService.metadata?.custom_service_type,
          amount: normalizedService.amount,
          discounted_amount: normalizedService.discounted_amount
        });
        
        // Se non ha payment_schedule, crea un oggetto vuoto
        if (!normalizedService.payment_schedule || !normalizedService.payment_schedule.installments) {
          normalizedService.payment_schedule = { installments: [] };
        } else {
          // Normalizza le installments
          normalizedService.payment_schedule = {
            installments: normalizedService.payment_schedule.installments.map(inst => ({
              type: inst.type || 'fixed',
              value: typeof inst.value === 'number' ? inst.value : 0,
              amount: typeof inst.amount === 'number' ? inst.amount : 0,
              daysAfterStart: inst.daysAfterStart || 0,
              description: inst.description || '',
              isConfigurable: inst.isConfigurable || false,
              timeUnit: inst.timeUnit,
              fixedDayOfMonth: inst.fixedDayOfMonth,
              nextMonth: inst.nextMonth,
              fixedDate: inst.fixedDate,
              // Finup R21
              awaiting_approval: (inst as any).awaiting_approval ?? false
            }))
          };
        }

        return normalizedService;
      }
      
      // Se non è un servizio personalizzato, applica solo normalizzazione standard
      // Se non ha payment_schedule, crea un oggetto vuoto
      if (!service.payment_schedule) {
        return {
          ...service,
          payment_schedule: { installments: [] }
        };
      }
      
      // payment_schedule should now always be an object with installments property
      let installments = [];
      if (service.payment_schedule.installments && Array.isArray(service.payment_schedule.installments)) {
        console.log('🔄 NORMALIZE: payment_schedule has installments for service:', service.service?.title || service.custom_service_name);
        installments = service.payment_schedule.installments;
      } else {
        console.log('⚠️ NORMALIZE: payment_schedule missing or invalid installments for service:', service.service?.title || service.custom_service_name, service.payment_schedule);
      }
      
      // Normalize the installments
      return {
        ...service,
        payment_schedule: {
          installments: installments.map(inst => ({
            type: inst.type || 'fixed',
            value: typeof inst.value === 'number' ? inst.value : 0,
            amount: typeof inst.amount === 'number' ? inst.amount : 0,
            daysAfterStart: inst.daysAfterStart || 0,
            description: inst.description || '',
            isConfigurable: inst.isConfigurable || false,
            timeUnit: inst.timeUnit,
            fixedDayOfMonth: inst.fixedDayOfMonth,
            nextMonth: inst.nextMonth,
            fixedDate: inst.fixedDate,
            // Finup R21: preserva il flag "data da destinarsi"
            awaiting_approval: (inst as any).awaiting_approval ?? false
          }))
        }
      };
    })));

    safeLog('✅ Services normalizzati per l\'invio:', JSON.stringify(normalizedServices, null, 2));
    
    // Final check for quote number before submission
    const submissionData = { ...formData };
    // IVA del cliente: le righe prendono l'aliquota del cliente (anche quelle
    // aggiunte dopo la spunta). Se l'aliquota non è ancora caricata si conserva
    // la scelta salvata: le righe hanno già l'aliquota dal salvataggio precedente.
    if (clientVatSupported) {
      submissionData.apply_client_vat = !!formData.apply_client_vat && (clientVatRate != null || !clientVatLoaded);
      if (clientVatApplied) normalizedServices.forEach((s: any) => { s.vat_rate = clientVatRate; });
    } else {
      delete (submissionData as { apply_client_vat?: boolean }).apply_client_vat;
    }
    // Indicazioni per il magazzino: si inviano solo se la funzione è attiva,
    // così spegnerla non cancella quanto già scritto sulle offerte
    if (warehouseActive) {
      submissionData.warehouse_info = cleanWarehouseInfo(formData.warehouse_info);
    } else {
      delete submissionData.warehouse_info;
    }
    if (nextQuoteNumber && !submissionData.quote_number) {
      console.log(`📋 Adding missing quote number ${nextQuoteNumber} to submission data`);
      submissionData.quote_number = nextQuoteNumber;
    }
    
    // Normalize UUID fields - convert empty strings to null for database compatibility
    const uuidFieldsToNormalize = [];
    if (submissionData.lead_id === '') {
      submissionData.lead_id = null;
      uuidFieldsToNormalize.push('lead_id');
    }
    if (submissionData.client_id === '') {
      submissionData.client_id = null;
      uuidFieldsToNormalize.push('client_id');
    }
    if (submissionData.assigned_to === '') {
      submissionData.assigned_to = null;
      uuidFieldsToNormalize.push('assigned_to');
    }

    if (uuidFieldsToNormalize.length > 0) {
      console.log('🔧 Normalized UUID fields from empty strings to null:', uuidFieldsToNormalize);
    }

    // Add billing settings
    submissionData.single_invoice_mode = singleInvoiceMode;
    // Use format to avoid timezone issues (toISOString converts to UTC which can shift the date)
    submissionData.single_invoice_date = singleInvoiceDate ? format(singleInvoiceDate, 'yyyy-MM-dd') : null;

    // Add commission override setting
    submissionData.override_commission_scaling = overrideCommissionScaling;
    
    // Log final submission data for debugging
    console.log('📤 Final submission data:', {
      quote_number: submissionData.quote_number,
      client_id: submissionData.client_id,
      title: submissionData.title,
      services_count: normalizedServices.length,
      commissionData: commissionData
    });

    // Call onSubmit with the normalized services, finalized form data, commission overrides, referrer IDs, and contact IDs
    await onSubmit(submissionData, normalizedServices, commissionData, dealReferrerIds, selectedContactIds);

    // If we got here, the save was successful
    // We can now delete the draft from localStorage
    clearDraftFromLocalStorage();
    
    // Also clear service-specific localStorage to prevent cart items from persisting
    if (initialData?.id) {
      localStorage.removeItem(`deal_${initialData.id}_services`);
    } else {
      localStorage.removeItem('deal_draft_services');
    }
  } catch (error) {
    console.error('Error saving deal:', error);
  } finally {
    setIsSaving(false);
  }
};

const handleBack = () => {
  onCancel();
};

const handleSaveSchedule = (installments: DealPaymentInstallment[]) => {
  // Verifica che l'indice sia valido e all'interno dei limiti dell'array
  if (selectedServiceIndex === null ||
      selectedServiceIndex < 0 ||
      selectedServiceIndex >= serviceItems.length) {
    console.warn("⚠️ handleSaveSchedule: indice del servizio non valido:", selectedServiceIndex);
    return;
  }

  safeLog('🔍 handleSaveSchedule chiamato con installments:', JSON.stringify(installments, null, 2));
  safeLog('🔍 serviceItems prima dell\'aggiornamento:', JSON.stringify(serviceItems, null, 2));

  // Crea una copia profonda di serviceItems per evitare problemi di riferimento
  const updatedServices = JSON.parse(JSON.stringify(serviceItems));

  // Crea una copia profonda delle installments
  const normalizedInstallments = JSON.parse(JSON.stringify(installments.map(inst => ({
    type: inst.type || 'fixed',
    value: typeof inst.value === 'number' ? inst.value : 0,
    amount: typeof inst.amount === 'number' ? inst.amount : 0,
    daysAfterStart: inst.daysAfterStart || 0,
    description: inst.description || '',
    isConfigurable: inst.isConfigurable || false,
    timeUnit: inst.timeUnit,
    fixedDayOfMonth: inst.fixedDayOfMonth,
    nextMonth: inst.nextMonth,
    fixedDate: inst.fixedDate,
    awaiting_approval: inst.awaiting_approval,
    // Scadenza PAGAMENTO fattura (distinta dall'emissione)
    payment_due_date: inst.payment_due_date,
    payment_terms_days: inst.payment_terms_days
  }))));

  // Verifica ancora una volta che il servizio esista
  if (!updatedServices[selectedServiceIndex]) {
    console.error("❌ handleSaveSchedule: servizio non trovato all'indice:", selectedServiceIndex);
    return;
  }

  // Aggiorna il servizio con le nuove installments
  updatedServices[selectedServiceIndex] = {
    ...updatedServices[selectedServiceIndex],
    payment_schedule: {
      installments: normalizedInstallments
    }
  };
  
  safeLog('🔍 serviceItems aggiornati:', JSON.stringify(updatedServices, null, 2));
  
  // Aggiorna lo state con i nuovi dati
  setServiceItems(updatedServices);
  setShowCalculator(false);
  setSelectedServiceIndex(null);
  
  // Verifica dopo l'aggiornamento (questo log potrebbe non mostrare i dati aggiornati
  // a causa della natura asincrona di setState)
  safeLog('⚠️ Nota: serviceItems potrebbe non essere ancora aggiornato a causa della natura asincrona di setState');
};


  // Navigate to the next step
  const goToNextStep = () => {
    // Validate step 1 fields
    if (currentStep === 1) {
      if (!formData.client_id) {
        toast.error(language === 'it' ? 'Seleziona un cliente per continuare' : 'Please select a client to continue');
        // Scroll/focus al campo cliente (CreatableSelect ha un input interno)
        document.querySelector<HTMLInputElement>('.react-select__input input, [class*="react-select"] input')?.focus();
        return;
      }
      if (!formData.title.trim()) {
        toast.error(language === 'it' ? 'Inserisci un titolo per l\'offerta' : 'Please enter a deal title');
        document.querySelector<HTMLInputElement>('input[placeholder*="titolo dell\'offerta" i], input[placeholder*="deal title" i]')?.focus();
        return;
      }
      setCurrentStep(2);
    }
  };

  // Navigate to the previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Funzione per gestire la creazione di un nuovo cliente
  const handleCreateClient = (inputValue: string) => {
    setNewClientName(inputValue);
    setShowQuickClientInput(true);
  };

  // Funzione per espandere il quick input al form completo
  const handleExpandToFullForm = (quickData: QuickClientData) => {
    // Chiudi il quick input
    setShowQuickClientInput(false);

    // Prepara i dati iniziali per il form completo
    setInitialClientFormData({
      business_name: quickData.business_name,
      entity_type: quickData.entity_type,
      email: quickData.email,
      phone: quickData.phone,
      whatsapp_number: quickData.whatsapp_number,
    });

    // Apri il form completo
    setShowClientForm(true);
  };

  // Funzione per gestire la selezione di un servizio come titolo
  const handleServiceSelection = (serviceId: string) => {
    const selectedService = services.find(s => s.id === serviceId);
    if (selectedService) {
      setFormData(prev => ({
        ...prev,
        title: selectedService.title,
        description: selectedService.short_description || selectedService.description || ''
      }));
      
      // Update the editor content as well
      if (editor && (selectedService.short_description || selectedService.description)) {
        editor.commands.setContent(selectedService.short_description || selectedService.description || '');
      }
    }
  };

  // Funzione che gestisce la sottomissione del quick client input
  const handleQuickClientSubmit = async (quickData: QuickClientData) => {
    try {
      // Convertiamo i dati del quick input nel formato completo del client
      const clientData = {
        business_name: quickData.business_name,
        entity_type: quickData.entity_type,
        email: quickData.email || '',
        phone: quickData.phone || '',
        whatsapp_number: quickData.whatsapp_number || '',
        stato_cliente: quickData.stato_cliente || null, // Usa lo stato selezionato dall'utente
        assigned_to: user?.id, // Assegna all'utente corrente
      };

      // Creiamo il nuovo cliente (skip workflow trigger perché la creazione avviene da offerta)
      const newClient = await useClientStore.getState().createClient(clientData, { skipWorkflowTrigger: true });

      if (newClient) {
        // Aggiorniamo il client_id nel form dell'offerta
        setFormData(prev => ({ ...prev, client_id: newClient.id }));

        // Chiudiamo il quick input
        setShowQuickClientInput(false);

        // Notifichiamo il successo
        console.log(language === 'it' ? 'Cliente salvato con successo!' : 'Client saved successfully!');

        // Ricarica la lista clienti
        fetchClientsForSelect();
      }
    } catch (error) {
      console.error('Error saving client:', error);

      // Mostriamo un messaggio di errore
      confirm({
        title: language === 'it' ? 'Errore' : 'Error',
        message: language === 'it'
          ? 'Si è verificato un errore durante il salvataggio del cliente. Riprova.'
          : 'An error occurred while saving the client. Please try again.',
        confirmText: 'OK',
        cancelText: '',
        confirmVariant: 'danger'
      });
    }
  };

  // Funzione che gestisce la sottomissione del form cliente
  const handleClientFormSubmit = async (clientData: any): Promise<boolean> => {
    try {
      // Assicurati che assigned_to sia impostato all'utente corrente se non specificato
      const dataWithAssignment = {
        ...clientData,
        assigned_to: clientData.assigned_to || user?.id
      };

      // Crea il nuovo cliente (skip workflow trigger perché la creazione avviene da offerta)
      const newClient = await useClientStore.getState().createClient(dataWithAssignment, { skipWorkflowTrigger: true });

      if (newClient) {
        // Aggiorna il client_id nel form dell'offerta
        setFormData(prev => ({ ...prev, client_id: newClient.id }));

        // Chiudi il form cliente
        setShowClientForm(false);
        setInitialClientFormData(null);

        // Notifichiamo il successo
        console.log(language === 'it' ? 'Cliente salvato con successo!' : 'Client saved successfully!');

        // Ricarica la lista clienti
        fetchClientsForSelect();

        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving client:', error);
      return false;
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Draft Notification Banner - Compact & Elegant */}
      {isResumingDraft && draftLastUpdated && (
        <div className="mb-3 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-amber-400 dark:border-amber-600 rounded text-xs flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            <span className="font-medium text-amber-900 dark:text-amber-100">
              {language === 'it' ? 'Bozza ripristinata' : 'Draft resumed'}
            </span>
            <span className="text-amber-700 dark:text-amber-300">•</span>
            <span className="text-amber-700 dark:text-amber-300">
              {draftLastUpdated}
            </span>
          </div>
          <button
            onClick={() => setIsResumingDraft(false)}
            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
            title={language === 'it' ? 'Nascondi' : 'Hide'}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* New Header with Step Indicator, Summary and Tabs */}
      <DealFormHeader
        currentStep={currentStep}
        formData={formData}
        serviceItems={serviceItems}
        dealReferrerIds={dealReferrerIds}
        activeTab={activeCommissionTab}
        onTabChange={setActiveCommissionTab}
        onGoToNextStep={goToNextStep}
        onGoToPreviousStep={goToPreviousStep}
        onNavigateBack={handleBack}
        onSubmit={handleSubmit}
        submitting={isSaving}
        language={language}
        translations={dealsTranslations[language]}
        onServiceSelect={setSelectedServiceIndex}
        onFormDataChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
        onRemoveService={removeService}
        draftSavedAt={initialData?.id ? null : draftSavedAt}
        isEditMode={!!initialData?.id}
      />

      {/* Tab Content - Now works in both steps */}

      {/* Step 1 content in 'deal' tab */}
      {currentStep === 1 && activeCommissionTab === 'deal' && (
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {/* Desktop: Two columns */}
          <div className="hidden md:grid md:grid-cols-3 gap-0 flex-1 min-h-0">
            {/* Left Column - Client Selection */}
            <div className="md:col-span-1 modal-lead-left-column flex flex-col h-full overflow-hidden">
              {/* Flow unico scrollabile: cliente + timeline note/attività + WhatsApp.
                  La DealClientSelectorCard è sticky in cima ma applica il bordo
                  + background blur SOLO quando l'utente sta effettivamente
                  scrollando (leftColumnScrolled). Senza scroll resta inline
                  senza separatore visivo. */}
              <div
                className="flex-1 overflow-y-auto min-h-0"
                onScroll={(e) => {
                  const scrolled = e.currentTarget.scrollTop > 4;
                  if (scrolled !== leftColumnScrolled) setLeftColumnScrolled(scrolled);
                }}
              >
                <div
                  className={`sticky top-0 z-10 transition-colors ${
                    leftColumnScrolled
                      ? 'bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-black/[0.06] dark:border-white/[0.08]'
                      : ''
                  }`}
                >
                  <DealClientSelectorCard
                    variant="desktop"
                    isDark={isDark}
                    language={language}
                    isSearching={isSearching}
                    clientsLoading={clientsLoading}
                    filteredAndSortedClientOptions={filteredAndSortedClientOptions}
                    allClientOptions={allClientOptions}
                    formData={formData}
                    setFormData={setFormData}
                    clientIdProp={clientId}
                    selectedClient={selectedClient}
                    setLoadedClient={setLoadedClient}
                    setSearchResults={setSearchResults}
                    setClientSearchInput={setClientSearchInput}
                    debouncedSearch={debouncedSearch}
                    users={users}
                    currentUserId={user?.id}
                    selectStyles={selectStyles}
                    handleCreateClient={handleCreateClient}
                    showQuickClientInput={showQuickClientInput}
                    setShowQuickClientInput={setShowQuickClientInput}
                    newClientName={newClientName}
                    setNewClientName={setNewClientName}
                    handleQuickClientSubmit={handleQuickClientSubmit}
                    handleExpandToFullForm={handleExpandToFullForm}
                    selectedContactIds={selectedContactIds}
                    setSelectedContactIds={setSelectedContactIds}
                    shippingLocationSlot={shippingLocationSlot}
                  />
                </div>

                <div className="px-3 pb-3 pt-2 space-y-2">

                  {selectedClient && (
                    <DealClientFeedTimeline
                      notes={notes}
                      activities={clientActivities}
                      recordings={clientRecordings}
                      language={language}
                      formatDate={formatDateNote}
                      getActivityIcon={getActivityIcon}
                      getActivityTypeLabel={getActivityTypeLabel}
                      getActivityStatusClass={getActivityStatusClass}
                    />
                  )}

                  {whatsAppConfigured && dealNotificationsEnabled && (
                    <div className="bg-white/60 dark:bg-white/[0.03] border border-black/[0.06] dark:border-white/[0.08] rounded-lg p-2.5">
                      <label htmlFor="send-notifications-desktop" className="flex items-center gap-2 cursor-pointer">
                        <input
                          id="send-notifications-desktop"
                          type="checkbox"
                          className="h-3.5 w-3.5 text-black focus:ring-black border-black/30 rounded"
                          checked={formData.send_notifications !== false}
                          onChange={(e) => setFormData({ ...formData, send_notifications: e.target.checked })}
                        />
                        <span className="text-[12px] font-medium text-black dark:text-white">
                          {t.form.notifications.label}
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Offer Details (only Title and Description) */}
            <DealStep1OfferDetailsPanel
              variant="desktop"
              toggleIdSuffix="desktop"
              isDark={isDark}
              language={language}
              t={t}
              useServiceAsTitle={useServiceAsTitle}
              setUseServiceAsTitle={setUseServiceAsTitle}
              services={services}
              formData={formData}
              setFormData={setFormData}
              editor={editor}
              selectStyles={selectStyles}
              handleServiceSelection={handleServiceSelection}
              onOpenAIAssistant={() => setShowAIAssistant(true)}
            />
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-4 p-4 overflow-y-auto h-full">
            <DealClientSelectorCard
              variant="mobile"
              isDark={isDark}
              language={language}
              isSearching={isSearching}
              clientsLoading={clientsLoading}
              filteredAndSortedClientOptions={filteredAndSortedClientOptions}
              allClientOptions={allClientOptions}
              formData={formData}
              setFormData={setFormData}
              clientIdProp={clientId}
              selectedClient={selectedClient}
              setLoadedClient={setLoadedClient}
              setSearchResults={setSearchResults}
              setClientSearchInput={setClientSearchInput}
              debouncedSearch={debouncedSearch}
              users={users}
              currentUserId={user?.id}
              selectStyles={selectStyles}
              handleCreateClient={handleCreateClient}
              showQuickClientInput={showQuickClientInput}
              setShowQuickClientInput={setShowQuickClientInput}
              newClientName={newClientName}
              setNewClientName={setNewClientName}
              handleQuickClientSubmit={handleQuickClientSubmit}
              handleExpandToFullForm={handleExpandToFullForm}
              selectedContactIds={selectedContactIds}
              setSelectedContactIds={setSelectedContactIds}
              shippingLocationSlot={shippingLocationSlot}
            />

            {/* Titolo + Descrizione (mobile) — stesso componente del desktop,
                variante senza sfondo grid */}
            <div className="glass-card rounded-lg p-4" style={{ zIndex: 900 }}>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-black dark:text-white" />
                <h3 className="text-lg font-medium text-black dark:text-white">{t.form.title}</h3>
              </div>
              <DealStep1OfferDetailsPanel
                variant="mobile"
                toggleIdSuffix="mobile"
                isDark={isDark}
                language={language}
                t={t}
                useServiceAsTitle={useServiceAsTitle}
                setUseServiceAsTitle={setUseServiceAsTitle}
                services={services}
                formData={formData}
                setFormData={setFormData}
                editor={editor}
                selectStyles={selectStyles}
                handleServiceSelection={handleServiceSelection}
                onOpenAIAssistant={() => setShowAIAssistant(true)}
              />
            </div>

            {/* WhatsApp notifications - Mobile */}
            {whatsAppConfigured && dealNotificationsEnabled && (
              <div className="glass-card rounded-lg p-4">
                <div className="flex items-center">
                  <input
                    id="send-notifications-mobile"
                    type="checkbox"
                    className="h-4 w-4 text-black focus:ring-black border-black/30 rounded"
                    checked={formData.send_notifications !== false}
                    onChange={(e) => setFormData({ ...formData, send_notifications: e.target.checked })}
                  />
                  <label htmlFor="send-notifications-mobile" className="ml-2 block text-sm font-medium text-black dark:text-white">
                    {t.form.notifications.label}
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 1: Billing tab — empty state estratto in DealStep1BillingEmpty */}
      {currentStep === 1 && activeCommissionTab === 'billing' && (
        <DealStep1BillingEmpty language={language} />
      )}

      {/* Step 1: Commissions & Referrers tab - Two columns layout */}
      {currentStep === 1 && activeCommissionTab === 'commissions' && (
        <DealStep1CommissionsTab
          isDark={isDark}
          language={language}
          referrers={referrers}
          dealReferrerIds={dealReferrerIds}
          setDealReferrerIds={setDealReferrerIds}
          selectStyles={selectStyles}
          dealId={initialData?.id}
          clientId={formData.client_id || undefined}
          assignedTo={formData.assigned_to || undefined}
          serviceItems={serviceItems}
          commissionData={commissionData}
          setCommissionData={setCommissionData}
          singleInvoiceMode={singleInvoiceMode}
          singleInvoiceDate={singleInvoiceDate}
          setSingleInvoiceDate={setSingleInvoiceDate}
          overrideCommissionScaling={overrideCommissionScaling}
          setOverrideCommissionScaling={setOverrideCommissionScaling}
          hasUserEditedCommission={hasUserEditedCommission}
          onUserEditCommission={() => setHasUserEditedCommission(true)}
        />
      )}

      {/* Step 2: Services */}
      {currentStep === 2 && (
        <>
          {/* Tab Content - Le tab sono ora nell'header */}
          {activeCommissionTab === 'deal' && (
            <div className="flex-1 overflow-hidden min-h-0">
              <EnhancedDealServiceSelector
                dealId={initialData?.id}
                services={services}
                paymentMethods={paymentMethods}
                selectedServices={serviceItems}
                onChange={setServiceItems}
                lockedVatRate={clientVatApplied ? clientVatRate : null}
                translations={t.form.services}
                formData={{...formData, clients: clients}}
                onFormDataChange={setFormData}
                externalSelectedServiceIndex={selectedServiceIndex}
                onExternalServiceIndexChange={setSelectedServiceIndex}
                onUnsavedChangesChange={setHasUnsavedServiceChanges}
                clientName={selectedClientName}
                dealSignedAt={initialData?.signed_at || null}
                onOpenBillingSettings={() => setActiveCommissionTab('billing')}
                onServiceSaved={(index) => {
                  // Mostra toast di successo
                  toast.success('Servizio salvato con successo', 3000);

                  // Imposta l'indice del servizio salvato per highlight
                  setSavedServiceIndex(index);

                  // Animazione checkmark sul pulsante
                  const saveButton = document.getElementById('save-service-button');
                  if (saveButton) {
                    const originalText = saveButton.innerHTML;
                    saveButton.innerHTML = '<span class="flex items-center gap-2"><svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg> Salvato!</span>';
                    saveButton.classList.add('bg-green-600', 'hover:bg-green-700');
                    saveButton.classList.remove('bg-black', 'hover:bg-gray-800');

                    setTimeout(() => {
                      saveButton.innerHTML = originalText;
                      saveButton.classList.remove('bg-green-600', 'hover:bg-green-700');
                      saveButton.classList.add('bg-black', 'hover:bg-gray-800');
                    }, 2000);
                  }

                  // Reset highlight dopo 2 secondi
                  setTimeout(() => setSavedServiceIndex(null), 2000);
                }}
              />
            </div>
          )}

          {/* Commissions Tab Content for Step 2 — stesso layout di Step 1 */}
          {activeCommissionTab === 'commissions' && (
            <DealStep1CommissionsTab
              isDark={isDark}
              language={language}
              referrers={referrers}
              dealReferrerIds={dealReferrerIds}
              setDealReferrerIds={setDealReferrerIds}
              selectStyles={selectStyles}
              dealId={initialData?.id}
              clientId={formData.client_id || undefined}
              assignedTo={formData.assigned_to || undefined}
              serviceItems={serviceItems}
              commissionData={commissionData}
              setCommissionData={setCommissionData}
              singleInvoiceMode={singleInvoiceMode}
              singleInvoiceDate={singleInvoiceDate}
              setSingleInvoiceDate={setSingleInvoiceDate}
              overrideCommissionScaling={overrideCommissionScaling}
              setOverrideCommissionScaling={setOverrideCommissionScaling}
              hasUserEditedCommission={hasUserEditedCommission}
              onUserEditCommission={() => setHasUserEditedCommission(true)}
            />
          )}
          {/* Billing Tab Content */}
          {activeCommissionTab === 'billing' && (
            <DealStep2BillingTab
              language={language}
              singleInvoiceMode={singleInvoiceMode}
              setSingleInvoiceMode={setSingleInvoiceMode}
              paymentMethods={paymentMethods as any}
              paymentMethodId={formData.payment_method_id}
              onPaymentMethodChange={(id) => setFormData(prev => ({ ...prev, payment_method_id: id }))}
              hasProductLines={serviceItems.some(s => (s as any).line_type === 'product')}
              serviceItems={serviceItems}
              services={services}
              onOpenLine={(index) => {
                setActiveCommissionTab('deal');
                setSelectedServiceIndex(index);
              }}
            />
          )}

          {/* Display acceptance/closed date if status is accepted */}
          {(formData.status === 'accepted' || initialData?.closed_at) && (
            <div className="glass-card rounded-lg mt-4">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5 text-black dark:text-white" />
                <h3 className="text-lg font-medium text-black dark:text-white">{t.signature.acceptanceDate}</h3>
              </div>

              <DatePicker
                selected={
                  initialData?.closed_at && !isNaN(new Date(initialData.closed_at).getTime())
                    ? new Date(initialData.closed_at)
                    : new Date()
                }
                onChange={(date) => {
                  if (date) {
                    const updates = {
                      closed_at: date.toISOString()
                    };
                    // Aggiorna il form data localmente
                    setFormData(prev => ({
                      ...prev,
                      ...updates
                    }));
                  }
                }}
                className="block w-full pl-3 pr-3 py-2 glass-card rounded-lg text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
                dateFormat="dd/MM/yyyy"
                isClearable={false}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                portalId="root-portal"
              />
            </div>
          )}
        </>
      )}

      {/* New Floating Action Bar Component */}
      <ResponsiveModal
        isOpen={showCalculator}
        onClose={() => {
          setShowCalculator(false);
          setSelectedServiceIndex(null);
        }}
        title="Configure Payment Schedule"
        size="lg"
      >
        {selectedServiceIndex !== null && selectedServiceIndex >= 0 && selectedServiceIndex < serviceItems.length && (
          <PaymentScheduleCalculator
            serviceAmount={
              serviceItems[selectedServiceIndex]?.discounted_amount ||
              serviceItems[selectedServiceIndex]?.amount ||
              0
            }
            existingInstallments={
              serviceItems[selectedServiceIndex]?.payment_schedule?.installments || []
            }
            // Senza questi due l'importo restava anonimo ("Importo servizio"),
            // mentre nel drawer la stessa riga si chiama col suo periodo.
            serviceType={
              (serviceItems[selectedServiceIndex]?.metadata as any)?.custom_service_type
              || services.find(s => s.id === serviceItems[selectedServiceIndex]?.service_id)?.service_type
            }
            billingMonths={
              (serviceItems[selectedServiceIndex]?.metadata as any)?.billing_interval_months
            }
            referenceDate={initialData?.signed_at || null}
            onSave={handleSaveSchedule}
            onClose={() => {
              setShowCalculator(false);
              setSelectedServiceIndex(null);
            }}
          />
        )}
      </ResponsiveModal>
      
      {/* Creazione di un nuovo cliente dall'offerta: drawer Notion-like (il wizard a passi è stato eliminato il 03/09/2026) */}
      <CreateClientDrawer
        isOpen={showClientForm}
        onClose={() => {
          setShowClientForm(false);
          setInitialClientFormData(null);
        }}
        initialData={initialClientFormData || { business_name: newClientName }}
        onComplete={handleClientFormSubmit}
      />

      {/* AI Assistant Modal */}
      <ChatGptPromptModal
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        onTextGenerated={(text) => {
          if (editor) {
            editor.commands.setContent(text);
            setFormData(prev => ({
              ...prev,
              description: text
            }));
          }
        }}
        currentText={formData.description}
        serviceTitle={formData.title}
        translations={{
          generateWithAI: language === 'it' ? 'Genera introduzione con IA' : 'Generate introduction with AI',
          promptLabel: language === 'it' ? 'Cosa vuoi generare?' : 'What do you want to generate?',
          promptPlaceholder: language === 'it' 
            ? 'Descrivi come vuoi che l\'IA introduca questa offerta commerciale...' 
            : 'Describe how you want the AI to introduce this commercial offer...',
          promptRequired: language === 'it' 
            ? 'Inserisci un prompt per generare l\'introduzione' 
            : 'Enter a prompt to generate the introduction',
          promptExamples: language === 'it' ? 'Esempi di prompt:' : 'Prompt examples:',
          promptExample1: language === 'it' 
            ? 'Scrivi un\'introduzione sintetica che presenti l\'offerta e il suo valore principale' 
            : 'Write a concise introduction presenting the offer and its main value',
          promptExample2: language === 'it' 
            ? 'Crea un\'introduzione che catturi l\'attenzione evidenziando il problema che risolviamo' 
            : 'Create an attention-grabbing introduction highlighting the problem we solve',
          promptExample3: language === 'it' 
            ? 'Scrivi un breve riepilogo dei vantaggi chiave di questa proposta commerciale' 
            : 'Write a brief summary of the key benefits of this commercial proposal',
          promptExample4: language === 'it' 
            ? 'Introduci l\'offerta sottolineando l\'unicità della nostra soluzione rispetto alla concorrenza' 
            : 'Introduce the offer emphasizing the uniqueness of our solution compared to competitors',
          generate: language === 'it' ? 'Genera introduzione' : 'Generate introduction',
          generating: language === 'it' ? 'Generazione in corso...' : 'Generating...',
          cancel: language === 'it' ? 'Annulla' : 'Cancel',
          currentText: language === 'it' ? 'Introduzione attuale:' : 'Current introduction:',
          serviceTitle: language === 'it' ? 'Titolo dell\'offerta:' : 'Deal title:',
          noText: language === 'it' ? 'Nessuna introduzione presente' : 'No introduction available',
          emptyResponseError: language === 'it' 
            ? 'Nessun testo generato. Riprova con un prompt diverso.' 
            : 'No text generated. Try again with a different prompt.'
        }}
      />
    </div>
  );
}